/**
 * ASCVD Established Cardiovascular Disease Study - Self-Contained Module
 *
 * Cohort A: For adults with a history of atherosclerotic cardiovascular disease
 * such as heart attacks or strokes
 */

import { defineStudy } from "./registry";
import { azascvdEstablishedScreeningQuestions } from "../../../data/pre-screener-config/AZASCVDEstablishedScreener";
import { ascvdEligibleZipCodes } from "../../../data/ascvd-study-zipcodes/ascvdZipCodes";

/**
 * Common question IDs that will be asked before study-specific questions
 * and should be filtered out to avoid duplication
 */
const COMMON_QUESTION_IDS = [
  "cardiovascular_event_history", // NEW: Routing question
  "ldl_cholesterol_check",
  "high_intensity_statin",
  "major_medical_conditions",
  "recent_medications",
  "height",
  "weight",
  "other_clinical_trials",
  "inPersonAppointments",
];

/**
 * ASCVD Established Cardiovascular Disease Study - Cohort A
 *
 * Priority: 1 (Highest - patients with established disease take priority)
 * Trial ID: DMASCVDAZD012A
 * Location: Nationwide (US)
 *
 * Key criteria:
 * - Age 18+
 * - History of heart attack in past 12 months OR stroke in past 12 months OR revascularization for PAD
 * - LDL 60+ or not recently tested
 * - Taking high-intensity statin
 * - Additional risk factors OR age 65+
 */
export const ascvdEstablishedStudy = defineStudy({
  id: "ascvd-established",
  name: "ASCVD Established Cardiovascular Disease Study (Cohort A)",
  trialId: "DMASCVDAZD012A",
  priority: 1, // Higher priority - for patients with established disease

  // Location-restricted - specific study sites
  eligibleZipCodes: Array.from(ascvdEligibleZipCodes),

  // All questions including common ones (will be filtered automatically)
  questions: azascvdEstablishedScreeningQuestions,

  // Questions to filter out (already asked in common stage)
  commonQuestionIds: COMMON_QUESTION_IDS,

  // ROUTING FILTER: Only consider this study for patients with RECENT cardiovascular events
  // This prevents patients with old events from being incorrectly routed here
  commonAnswerRequirements: {
    cardiovascular_event_history: ["recent_event"],
  },

  metadata: {
    sponsor: "AstraZeneca",
    description:
      "Investigating an investigational medication for preventing major cardiovascular events in adults with a history of atherosclerotic cardiovascular disease",
  },
});
